import React, { useEffect, useState } from 'react';
import { LayoutDashboard, Waves, Settings, BarChart3, Layers, BookOpen, CreditCard, LogOut, ShieldCheck, Lock, MessageCircle, Sparkles, Users, Handshake, X, Globe, Zap, Link2, Activity } from 'lucide-react';
import { auth, db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { OptiVeloLogo } from './OptiVeloLogo';
import { useLanguage } from '../src/lib/LanguageContext';
import { LanguageSelector } from './LanguageSelector';

export type ViewState = 'dashboard' | 'optimizer' | 'smart_copywriter' | 'landing_pages' | 'reports' | 'settings' | 'guide' | 'subscription' | 'terms' | 'privacy' | 'contact' | 'networking' | 'domain_settings' | 'integrations' | 'quantum_connector' | 'admin_status';

interface SidebarProps {
  currentView: ViewState;
  onChangeView: (view: ViewState) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

// v2.0.1 - Quantum Intelligence Active - OptiVelo
export const Sidebar: React.FC<SidebarProps> = ({ currentView, onChangeView, isOpen, onClose }) => {
  const [userData, setUserData] = useState<any>(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    if (!auth.currentUser) return;

    const unsubscribe = onSnapshot(doc(db, 'users', auth.currentUser.uid), (doc) => {
      if (doc.exists()) {
        setUserData(doc.data());
      }
    });

    return () => unsubscribe();
  }, []);

  const [simulationMode, setSimulationMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('OPTIVELO_SIMULATION_MODE') === 'true';
    }
    return false;
  });

  const toggleSimulation = () => {
    const next = !simulationMode;
    setSimulationMode(next);
    localStorage.setItem('OPTIVELO_SIMULATION_MODE', String(next));
    window.dispatchEvent(new CustomEvent('simulation-mode-changed', { detail: next }));
    window.location.reload(); // Recarregar para garantir que todos os componentes apliquem a lógica
  };

  const isSimulatingClient = userData?.role === 'admin' && simulationMode;

  const planName = isSimulatingClient
    ? (t('sidebar.trialPlan') + ' (' + (t('sidebar.simulateClient') === 'Simular Cliente' ? 'Simulação' : 'Simulation') + ')')
    : userData?.role === 'admin' 
      ? t('sidebar.adminPlan')
      : userData?.subscriptionStatus === 'trial'
        ? t('sidebar.trialPlan')
        : userData?.subscriptionStatus 
          ? userData.subscriptionStatus.charAt(0).toUpperCase() + userData.subscriptionStatus.slice(1)
          : t('sidebar.freePlan');

  const getTrialDaysLeft = () => {
    if (!userData?.trialEndDate) return 0;
    const end = userData.trialEndDate.toDate();
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const trialDays = getTrialDaysLeft();

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[45] lg:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`w-56 h-screen glass-nav flex flex-col py-3 px-2 fixed left-0 top-0 z-50 transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex items-center justify-between mb-2 px-2">
        <div 
          onClick={() => {
            onChangeView('dashboard');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
            if (onClose) onClose();
          }}
          className="flex items-center cursor-pointer group"
        >
          <OptiVeloLogo variant="full" size={28} className="group-hover:scale-105 transition-transform" />
        </div>
          <button 
            onClick={onClose}
            className="lg:hidden p-2 text-slate-500 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-1.5 mb-3 px-3">
          <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></div>
          <span className="text-[11px] font-medium text-emerald-500 uppercase tracking-wider">OptiVelo Live</span>
        </div>

      <nav className="flex-1 min-h-0 space-y-3 overflow-y-auto custom-scrollbar pr-1">
        <div>
          <p className="micro-label mb-0.5 px-2 text-[10px] text-slate-300">{t('sidebar.plataforma')}</p>
          <ul className="space-y-1">
            <SidebarItem 
              icon={LayoutDashboard} 
              label={t('sidebar.dashboard')} 
              active={currentView === 'dashboard'} 
              onClick={() => onChangeView('dashboard')}
            />
            <SidebarItem 
              icon={Zap} 
              label={t('sidebar.optimizer')} 
              active={currentView === 'optimizer'} 
              onClick={() => onChangeView('optimizer')}
            />
            <SidebarItem 
              icon={Sparkles} 
              label={t('sidebar.copywriter')} 
              active={currentView === 'smart_copywriter'} 
              onClick={() => onChangeView('smart_copywriter')}
            />
            <SidebarItem 
              icon={Layers} 
              label={t('sidebar.landing_pages')} 
              active={currentView === 'landing_pages'}
              onClick={() => onChangeView('landing_pages')}
            />
            <SidebarItem 
              icon={Globe} 
              label={t('sidebar.quantum')} 
              active={currentView === 'quantum_connector'}
              onClick={() => onChangeView('quantum_connector')}
              highlight
              locked={(userData?.role !== 'admin' || simulationMode) && !['scale', 'enterprise'].includes(userData?.subscriptionStatus)}
            />
            <SidebarItem 
              icon={Link2} 
              label={t('sidebar.integrations')} 
              active={currentView === 'integrations'}
              onClick={() => onChangeView('integrations')}
            />
            <SidebarItem 
              icon={BarChart3} 
              label={t('sidebar.reports')} 
              active={currentView === 'reports'}
              onClick={() => onChangeView('reports')}
            />
            <SidebarItem 
              icon={Handshake} 
              label={t('sidebar.networking')} 
              active={currentView === 'networking'}
              onClick={() => onChangeView('networking')}
              locked={(userData?.role !== 'admin' || simulationMode) && !['scale', 'enterprise'].includes(userData?.subscriptionStatus)}
            />
          </ul>
        </div>

        {userData?.role === 'admin' && (
          <div>
            <p className="micro-label mb-0.5 px-2 text-[10px] text-slate-300 uppercase tracking-widest font-semibold text-indigo-400">
              {language === 'en' ? 'Administration' : (language === 'es' ? 'Administración' : 'Administração')}
            </p>
            <ul className="space-y-1">
              <SidebarItem 
                icon={Activity} 
                label={language === 'en' ? 'System Status' : (language === 'es' ? 'Estado del Sistema' : 'Status do Sistema')}
                active={currentView === 'admin_status'}
                onClick={() => onChangeView('admin_status')}
                highlight
              />
            </ul>
          </div>
        )}

        <div>
          <p className="micro-label mb-0.5 px-2 text-[10px] text-slate-300">{t('sidebar.gestao')}</p>
          <ul className="space-y-1">
            <SidebarItem 
              icon={CreditCard} 
              label={t('sidebar.subscription')} 
              active={currentView === 'subscription'}
              onClick={() => onChangeView('subscription')}
              highlight={userData?.subscriptionStatus === 'trial'}
            />
            <SidebarItem 
              icon={Globe} 
              label={t('sidebar.domain')} 
              active={currentView === 'domain_settings'}
              onClick={() => onChangeView('domain_settings')}
              highlight={userData?.subscriptionStatus === 'enterprise' || userData?.role === 'admin'}
            />
            <SidebarItem 
              icon={Settings} 
              label={t('sidebar.settings')} 
              active={currentView === 'settings'}
              onClick={() => onChangeView('settings')}
            />
            <SidebarItem 
              icon={ShieldCheck} 
              label={t('sidebar.terms')} 
              active={currentView === 'terms'}
              onClick={() => onChangeView('terms')}
            />
            <SidebarItem 
              icon={Lock} 
              label={t('sidebar.privacy')} 
              active={currentView === 'privacy'}
              onClick={() => onChangeView('privacy')}
            />
          </ul>
        </div>

        <div>
          <p className="micro-label mb-0.5 px-2 text-[10px] text-slate-300">{t('sidebar.suporte')}</p>
          <ul className="space-y-1">
            <SidebarItem 
              icon={BookOpen} 
              label={t('sidebar.guide')} 
              active={currentView === 'guide'}
              onClick={() => onChangeView('guide')}
            />
            <SidebarItem 
              icon={MessageCircle} 
              label={t('sidebar.contact')} 
              active={currentView === 'contact'}
              onClick={() => onChangeView('contact')}
            />
          </ul>
        </div>
      </nav>

      <div className="mt-auto pt-2 border-t border-white/5 space-y-1.5">
        <div className="px-2 flex items-center justify-between text-[12px] font-bold text-slate-600 uppercase tracking-widest">
          <span>{t('sidebar.system')}</span>
          <span className="text-emerald-500">{t('sidebar.synchronized')}</span>
        </div>
        
        {userData?.role === 'admin' && (
          <button 
            onClick={toggleSimulation}
            className={`w-full p-2 border rounded-xl flex items-center gap-2 transition-all ${
              simulationMode 
                ? 'bg-amber-500/10 border-amber-500/20 text-amber-500' 
                : 'bg-white/[0.03] border-white/5 text-slate-500 hover:bg-white/5'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            <span className="text-[12px] font-bold uppercase tracking-tight">
              {simulationMode ? t('sidebar.clientActive') : t('sidebar.simulateClient')}
            </span>
          </button>
        )}

        <div className="p-2 bg-white/[0.03] border border-white/5 rounded-xl group cursor-pointer hover:bg-white/[0.05] transition-all">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-indigo-500/10 flex items-center justify-center">
              <span className="text-[12px] font-bold text-indigo-400">
                {(userData?.displayName || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">
                {userData?.displayName || (t('sidebar.freePlan') === 'Free' ? 'User' : 'Usuario')}
              </p>
              <p className="text-[11px] text-slate-500 truncate">
                {t('sidebar.subscription')}: {planName} {userData?.subscriptionStatus === 'trial' && `(${trialDays}${t('sidebar.daysRemaining')})`}
              </p>
            </div>
            <button 
              onClick={() => auth.signOut()}
              className="p-2 text-slate-500 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Language selector in sidebar foot */}
        <div className="pt-1.5">
          <LanguageSelector variant="sidebar" />
        </div>
      </div>
    </aside>
    </>
  );
};

const SidebarItem: React.FC<{ icon: React.ElementType, label: string, active?: boolean, onClick?: () => void, locked?: boolean, highlight?: boolean }> = ({ icon: Icon, label, active, onClick, locked, highlight }) => {
  const { t } = useLanguage();
  
  const handleLockedClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('show-toast', { 
      detail: { 
        message: t('sidebar.accessPremium'), 
        type: 'info' 
      } 
    }));
  };

  return (
    <li>
      <button
        onClick={locked ? handleLockedClick : onClick}
        className={`w-full flex items-center gap-2.5 py-3 lg:py-2 px-4 lg:px-3 rounded-xl transition-all duration-200 group relative cursor-pointer active:scale-[0.98] ${
          active 
            ? 'bg-indigo-500/20 text-white border border-indigo-500/20 shadow-lg shadow-indigo-500/5' 
            : highlight
              ? 'bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20'
              : 'text-slate-400 hover:bg-white/5 hover:text-white'
        } ${locked ? 'opacity-50 cursor-default' : ''}`}
      >
        <div className={`p-1.5 rounded-lg transition-colors ${active ? 'bg-indigo-500/20 text-indigo-400' : 'bg-transparent text-slate-400 group-hover:text-white'}`}>
          <Icon className={`w-3.5 h-3.5 transition-transform duration-200 ${active ? 'scale-110' : 'group-hover:scale-110'}`} />
        </div>
        <span className={`text-[12px] font-bold tracking-tight ${active ? 'text-white' : 'group-hover:text-white'}`}>{label}</span>
        {highlight && !active && <div className="ml-auto w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>}
        {locked && <Lock className="ml-auto w-3 h-3 text-slate-600" />}
        {active && !locked && <div className="ml-auto w-1 h-1 bg-indigo-400 rounded-full shadow-[0_0_8px_rgba(129,140,248,1)]"></div>}
        
        {/* Tooltip for locked items */}
        {locked && (
          <div className="absolute left-full ml-2 px-2 py-1 bg-slate-900 border border-white/10 rounded text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all whitespace-nowrap z-[60]">
            {t('sidebar.accessPremium')}
          </div>
        )}
      </button>
    </li>
  );
};
